/**
 * Petty Cash Register — every movement, chronologically, with the voucher that raised it.
 *
 * The standard report GAP requested alongside the voucher workflow: replaces reading each float's
 * statement one at a time with one filterable ledger across every float.
 */
import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Download, ArrowDownCircle, ArrowUpCircle, Undo2 } from 'lucide-react';

import { pettyCashVoucherApi } from '@/api/pettyCashVoucher.api';
import { pettyCashApi } from '@/api/pettyCash.api';
import { exportToCsv } from '@/utils/exportCsv';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';

import type { PettyCashFloatListDto } from '@/types/pettyCash.types';
import type {
  PettyCashRegisterRowDto, PettyCashRegisterFilterDto, PettyCashMovementType,
} from '@/types/pettyCashVoucher.types';

const MOVEMENT_META: Record<PettyCashMovementType, { label: string; icon: typeof ArrowDownCircle; color: string; sign: 1 | -1 }> = {
  TOPUP:   { label: 'Top-up',  icon: ArrowDownCircle, color: 'text-emerald-600', sign: 1 },
  PAYMENT: { label: 'Payment', icon: ArrowUpCircle,   color: 'text-red-600',     sign: -1 },
  RETURN:  { label: 'Return',  icon: Undo2,           color: 'text-amber-600',   sign: -1 },
};

const fmt = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

export const PettyCashRegisterPage = () => {
  const { hasPermission } = usePermission();
  const canView = hasPermission(Permissions.PTY_FLOAT_VIEW);

  const [floats, setFloats] = useState<PettyCashFloatListDto[]>([]);
  const [rows,   setRows]   = useState<PettyCashRegisterRowDto[]>([]);
  const [total,  setTotal]  = useState(0);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<PettyCashRegisterFilterDto>({ pageSize: 200 });

  useEffect(() => {
    if (!canView) return;
    pettyCashApi.list().then(r => setFloats(r.data?.data ?? [])).catch(() => {});
  }, [canView]);

  const load = useCallback(() => {
    if (!canView) return;
    setLoading(true);
    pettyCashVoucherApi.getRegister(filter)
      .then(r => {
        const d = r.data.data;
        setRows(d?.items ?? []);
        setTotal(d?.totalCount ?? 0);
      })
      .catch(() => { setRows([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [canView, filter]);

  useEffect(() => { load(); }, [load]);

  // Opening and closing balance for the range (QC P6-133). Only for one float with every movement
  // of the range loaded and no type filter — balanceAfter is a running balance per float, so any
  // other view would mix boxes or skip movements. Rows arrive newest first.
  const balances = filter.floatId && !filter.movementType && rows.length > 0 && rows.length === total
    ? (() => {
        const oldest = rows[rows.length - 1];
        return {
          opening: oldest.balanceAfter - MOVEMENT_META[oldest.movementType].sign * oldest.amount,
          closing: rows[0].balanceAfter,
        };
      })()
    : null;

  const handleExport = () => {
    exportToCsv(`petty-cash-register-${new Date().toISOString().slice(0, 10)}.csv`, rows.map(r => ({
      Date: r.movementDate, Voucher: r.voucherNumber ?? '', Float: r.floatCode, Type: r.movementType,
      Account: r.counterGlAccountName ?? '', Amount: r.amount, 'Balance After': r.balanceAfter,
      Reference: r.reference ?? '', Description: r.description ?? '', Status: r.workflowStatus ?? '',
      'Posted By': r.postedByName ?? '',
    })));
  };

  if (!canView) {
    return (
      <div className="flex items-center justify-center h-full text-slate-400">
        <p className="text-sm">You do not have permission to view the petty cash register.</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex-shrink-0 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-slate-900 dark:text-white">Petty Cash Register</h1>
            <p className="text-xs text-slate-400">{total} movement{total === 1 ? '' : 's'}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} disabled={loading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 dark:text-gray-400
                         hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
            <button onClick={handleExport} disabled={rows.length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 dark:text-gray-400
                         hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-40">
              <Download size={14} /> Export
            </button>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select value={filter.floatId ?? ''} onChange={e => setFilter(f => ({ ...f, floatId: e.target.value || undefined }))}
            className="h-8 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 text-xs text-slate-700 dark:text-slate-200">
            <option value="">All floats</option>
            {floats.map(f => <option key={f.id} value={f.id}>{f.floatCode} — {f.floatName}</option>)}
          </select>
          <select value={filter.movementType ?? ''} onChange={e => setFilter(f => ({ ...f, movementType: (e.target.value || undefined) as PettyCashMovementType }))}
            className="h-8 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 text-xs text-slate-700 dark:text-slate-200">
            <option value="">All types</option>
            <option value="TOPUP">Top-up</option>
            <option value="PAYMENT">Payment</option>
            <option value="RETURN">Return</option>
          </select>
          <input type="date" value={filter.dateFrom ?? ''} onChange={e => setFilter(f => ({ ...f, dateFrom: e.target.value || undefined }))}
            className="h-8 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 text-xs text-slate-700 dark:text-slate-200" />
          <span className="text-xs text-slate-400">to</span>
          <input type="date" value={filter.dateTo ?? ''} onChange={e => setFilter(f => ({ ...f, dateTo: e.target.value || undefined }))}
            className="h-8 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 text-xs text-slate-700 dark:text-slate-200" />
        </div>

        {balances ? (
          <div className="mt-3 flex flex-wrap gap-6 text-xs text-slate-600 dark:text-slate-300">
            <span>Opening balance <span className="ml-1 font-mono font-semibold text-slate-800 dark:text-slate-100">{fmt(balances.opening)}</span></span>
            <span>Closing balance <span className="ml-1 font-mono font-semibold text-slate-800 dark:text-slate-100">{fmt(balances.closing)}</span></span>
          </div>
        ) : (
          <p className="mt-3 text-xs text-slate-400">Pick one float (and no type filter) to see the opening and closing balance for the range.</p>
        )}
      </div>

      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-48"><Spinner size="md" /></div>
        ) : rows.length === 0 ? (
          <div className="flex items-center justify-center h-48 text-slate-400">
            <p className="text-sm">No movements match these filters.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/90 backdrop-blur-sm border-b border-slate-200 dark:border-slate-700">
              <tr className="text-left text-xs uppercase text-slate-500 dark:text-gray-400">
                <th className="px-4 py-2.5 font-medium">Date</th>
                <th className="px-4 py-2.5 font-medium">Voucher</th>
                <th className="px-4 py-2.5 font-medium">Float</th>
                <th className="px-4 py-2.5 font-medium">Type</th>
                <th className="px-4 py-2.5 font-medium">Account</th>
                <th className="px-4 py-2.5 font-medium">Description</th>
                <th className="px-4 py-2.5 font-medium text-right">Amount</th>
                <th className="px-4 py-2.5 font-medium text-right">Balance</th>
                <th className="px-4 py-2.5 font-medium">Posted By</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {rows.map(r => {
                const meta = MOVEMENT_META[r.movementType];
                const Icon = meta.icon;
                return (
                  <tr key={r.movementId} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                    <td className="px-4 py-2.5 text-xs text-slate-600 dark:text-slate-300">{fmtDate(r.movementDate)}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-blue-700 dark:text-blue-400">
                      {r.voucherNumber ?? <span className="text-slate-300 dark:text-slate-600 italic">legacy</span>}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-700 dark:text-slate-200">{r.floatCode}</td>
                    <td className="px-4 py-2.5">
                      <span className={cn('inline-flex items-center gap-1 text-xs font-medium', meta.color)}>
                        <Icon size={13} /> {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-500 dark:text-gray-400 truncate max-w-[200px]">
                      {r.counterGlAccountName ?? '—'}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-500 dark:text-gray-400 truncate max-w-[220px]">
                      {r.description ?? r.reference ?? '—'}
                    </td>
                    <td className={cn('px-4 py-2.5 text-right font-mono text-xs font-medium', meta.color)}>
                      {meta.sign > 0 ? '+' : '−'}{fmt(r.amount)}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs text-slate-700 dark:text-slate-200">
                      {fmt(r.balanceAfter)}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-500 dark:text-gray-400">{r.postedByName ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
